/**
 * De QR-code van een kamer als PNG-bestand opslaan.
 *
 * De server levert de code als SVG. Dat is scherp op het scherm, maar een SVG
 * is een slechte bijlage: WhatsApp, de fotorol en de meeste printdialogen doen
 * er niets mee. Daarom tekenen we hem hier op een canvas en slaan we een PNG op.
 *
 * Waarom 1024 pixels: groot genoeg om af te drukken op A4 zonder blokjes, en
 * klein genoeg om te versturen. De QR zelf is maar een paar tientallen modules
 * breed, dus opschalen kost geen scherpte.
 */

const PNG_FORMAAT = 1024;

/** Fout die de knop kan tonen. Alle andere fouten komen hier ook als deze uit. */
export class QrDownloadFout extends Error {}

/**
 * Zet de SVG-tekst om naar een PNG-blob.
 *
 * De breedte en hoogte worden in de SVG gezet voordat hij naar een blob gaat.
 * Zonder die attributen weigert Safari een SVG in een canvas te tekenen: het
 * plaatje krijgt dan hoogte nul en je houdt een leeg vlak over.
 */
async function svgNaarPng(svg: string): Promise<Blob> {
  const gemeten = svg.replace(
    /<svg\b([^>]*)>/,
    (_hele, attributen: string) =>
      `<svg${attributen.replace(/\s(width|height)="[^"]*"/g, '')} width="${PNG_FORMAAT}" height="${PNG_FORMAAT}">`,
  );

  const svgUrl = URL.createObjectURL(new Blob([gemeten], { type: 'image/svg+xml' }));
  try {
    const plaatje = await new Promise<HTMLImageElement>((klaar, mislukt) => {
      const img = new Image();
      img.onload = () => klaar(img);
      img.onerror = () => mislukt(new QrDownloadFout('De QR-code kon niet worden geladen.'));
      img.src = svgUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = PNG_FORMAAT;
    canvas.height = PNG_FORMAAT;
    const tekenvlak = canvas.getContext('2d');
    if (tekenvlak === null) {
      throw new QrDownloadFout('Deze browser kan geen afbeelding maken.');
    }
    // Wit onderleggen. Een PNG met een doorzichtige achtergrond wordt in een
    // donkere weergave zwart op zwart, en dan scant geen enkele telefoon hem.
    tekenvlak.fillStyle = '#FFFFFF';
    tekenvlak.fillRect(0, 0, PNG_FORMAAT, PNG_FORMAAT);
    tekenvlak.drawImage(plaatje, 0, 0, PNG_FORMAAT, PNG_FORMAAT);

    return await new Promise<Blob>((klaar, mislukt) => {
      canvas.toBlob((blob) => {
        if (blob === null) {
          mislukt(new QrDownloadFout('De afbeelding kon niet worden opgeslagen.'));
          return;
        }
        klaar(blob);
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/** Duwt een blob als bestand naar de downloadmap van de browser. */
function bewaarBestand(blob: Blob, bestandsnaam: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = bestandsnaam;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Pas opruimen als de browser het bestand heeft opgepakt. Direct intrekken
  // laat de download in Chrome soms halverwege stranden.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Haalt de QR-code op en slaat hem op als PNG.
 *
 * `pad` is het pad van het SVG-endpoint, `kamerId` komt in de bestandsnaam
 * zodat twee kamers naast elkaar niet dezelfde naam krijgen.
 */
export async function bewaarQrAlsPng(pad: string, kamerId: string): Promise<void> {
  const antwoord = await fetch(pad);
  if (!antwoord.ok) {
    throw new QrDownloadFout('De QR-code is niet meer op te halen. Is de kamer verlopen?');
  }
  const png = await svgNaarPng(await antwoord.text());
  bewaarBestand(png, `askgio-tolk-qr-${kamerId}.png`);
}
